import React from 'react';
import { Typography, List, ListItem, ListItemText, Divider, Chip } from '@mui/material';
import HelpSidebar from './HelpSidebar';
import AskAardvarkHelpHint from './AskAardvarkHelpHint';
import { buildLegalContextSummary } from '../content/countryPolicyContent';
import { documentUpload } from '../utils/userFacingCopy';

interface OrganisationLeaveHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export const OrganisationLeaveHelpSidebar: React.FC<OrganisationLeaveHelpSidebarProps> = ({
  isExpanded,
  onToggle,
}) => (
  <HelpSidebar
    isExpanded={isExpanded}
    onToggle={onToggle}
    title="Organisation Leave Help"
  >
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
      The blue information banner under the page title gives a quick summary of scope (organisation-wide leave for the selected dates and filters). This help panel has the full detail. Use the search and filters in the card below the banner to narrow results; they apply to all tables below.
    </Typography>
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
      <strong>Access:</strong> Organisation Leave is available to <strong>Administrators</strong>, <strong>Senior Managers</strong>, and users in a <strong>Finance</strong> department (for example, "Finance Team"). Organisation Dashboard access remains admin/senior-manager only.
    </Typography>
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
      {buildLegalContextSummary()} This page presents policy outcomes using neutral labels.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      FILTERS & DATA
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Search and filters"
          secondary="There is no separate year dropdown: From and To default to the current fiscal year (full year). Narrow the dates to filter the main charts and 'All leave data' table to leave that overlaps that range. If you change From to a date in a different fiscal year, the page reloads data for that fiscal year and resets the range to that full year. Search matches employee name and business unit across all tables. Use the country, department, and leave type filters to focus results. Leave types are: Annual Leave, Family Responsibility Leave, Sick Leave, Microsoft Exam Leave, Time off in Lieu, Parental Leave. Country and department filters also apply to the statutory and policy usage tables (country uses each employee's company country from the usage/statutory data). Leave type filter applies to the main 'All leave data' table and charts only."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Leave by type (charts)"
          secondary="The collapsible 'Leave by type' section shows two charts. At the top: a pie chart of total leave days by type (organisation-wide mix). Below: a horizontal stacked bar chart—one bar per employee, each segment is leave type (days taken). Both use the same filters as the table. Expand the section to view."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="All leave data (table)"
          secondary="One row per occurrence—each approved leave request and each month of imported balance (e.g. 'Oct 2025', 'Mar 2026') appear as separate rows. Only rows with leave actually taken (no zero-day entries). Period column shows the request date range or month/year for balance data: a month (e.g. Oct 2025) means imported leave balance; a date range means leave approved in this system. Sort by any column (click the header); sorting by Period puts approved request dates first, then imported rows (imported always behind). 'Statutory entitlement met' shows Yes when the employee has met their country-specific statutory annual leave target for the year (ZA/IN: 15 days; GB: 28 days). For approved sick leave, the **Sick leave notes** column lets you view or download attached evidence when present. For monitoring who has not yet met statutory, use the Statutory annual leave table below."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Download leave report (Excel)"
          secondary="Use the Download leave report button to export the currently filtered date-range results (From/To, search, country, department, and leave type) as an Excel workbook (.xlsx)—the same filtered rows as the All leave data table (one shared filter in the app), including all departments and business units. Columns are Employee, Employee Number, Business Unit, Department, Leave Type, From Date, To Date, and Days Taken. Days Taken is the stored working-day total for each row (weekdays minus public holidays for approved bookings)—the same as the table, not prorated to the filter window. Rows appear when their period overlaps From/To: approved leave uses request dates; imported monthly balance rows use the first and last day of that month in From Date and To Date. The button is disabled only when no exportable rows match the current filters. The file name includes the selected date range."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      STATUTORY ANNUAL LEAVE — RAG STATUS
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5 }}>
      This table shows every employee’s progress toward their country-specific statutory annual leave target. Status is <strong>time-aware</strong>: risk increases when many days remain to take and the end of the fiscal year is near.
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Chip label="Met" size="small" color="success" sx={{ flexShrink: 0, height: 22 }} />
        <ListItemText
          primary="Met (Green)"
          secondary="Employee has met the statutory annual leave target for their country in the fiscal year. No action needed."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Chip label="On track" size="small" color="success" sx={{ flexShrink: 0, height: 22 }} />
        <ListItemText
          primary="On track (Green)"
          secondary="Below statutory target, but the required rate (days per month to reach target) is low—plenty of time left in the year. No urgent action; the statutory table may still show days to take."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Chip label="At risk" size="small" color="warning" sx={{ flexShrink: 0, height: 22 }} />
        <ListItemText
          primary="At risk (Amber)"
          secondary="Below statutory target and the required rate is moderate. Follow up to encourage booking leave."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Chip label="High risk" size="small" color="error" sx={{ flexShrink: 0, height: 22 }} />
        <ListItemText
          primary="High risk (Red)"
          secondary="Either less than one month left in the fiscal year with days still to take, or the required leave rate (days per month to reach target) is very high. Prioritise these employees for follow-up."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      POLICY USAGE TABLES
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5 }}>
      The tables below show leave usage against policy limits. Only employees who have actually taken that leave type appear (smaller, faster to load).
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Family responsibility leave (country-specific cap per year)"
          secondary="Only employees who have used family responsibility leave in the period. Cap varies by country (e.g. 3 days for South Africa, 5 days for India). Status: 'At cap' = reached policy cap; 'Over cap' = exceeded. UK employees are shown as 'Exempt' because UK dependants/family leave does not map to this fixed-cap table."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Sick leave (30 days per three-year period)"
          secondary={
            <>
              Only employees who have used sick leave appear. When you set <strong>From</strong> and <strong>To</strong> dates,
              the list and <strong>Days in period</strong> column reflect sick leave in that window only (same overlap as the charts above).
              Without a custom range, <strong>Days taken</strong> covers the current three-year sick leave period.
              <br />
              <br />
              <strong>Cap</strong> and <strong>Status</strong> always reflect the 30-day limit for each person&apos;s
              <em>current</em> three-year period—they do not change when you narrow the dates, in the same way statutory
              annual leave status always reflects the full fiscal year.
              <br />
              <br />
              <strong>Status</strong> values: blank = within limit; <strong>Approaching cap</strong> = 80% or more of the
              limit used; <strong>Over cap</strong> = limit exceeded. UK and India employees are <strong>Exempt</strong>
              (local policy applies; no cap shown).
            </>
          }
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      BOOK LEAVE ON BEHALF (ADMINISTRATORS ONLY)
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      Use the <strong>Book leave on behalf</strong> button (top of the page) to create and submit leave for another employee (e.g. when they cannot do it themselves). Select the employee, leave type, dates, and comments. When the start and end date are the <strong>same day</strong>, choose <strong>Full day</strong> or <strong>Half day</strong> (0.5 days). When the range is <strong>more than one day</strong>, tick <strong>This range includes a half day</strong> if the total should be the working days in the range <strong>minus 0.5</strong>. Upload evidence if required (same file types as My Leave: {documentUpload.allowedTypesShortLabel}; {documentUpload.maxFileSizeLabel} per file). The request is created and <strong>auto-approved</strong>—no manager approval step. The blue message shows how many days the request is for and, for annual leave, how many days they have already taken this year. The employee sees the leave with &quot;Created on your behalf by [your name]&quot;. All actions are audited (Leave Audit Log: filter by <strong>Leave Request Created On Behalf</strong>). Editing or cancelling approved leave that has already started or is in the past is done by the employee from <strong>My Leave</strong>; they must give a reason and an Administrator approves or rejects the change (see My Leave help).
    </Typography>

    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 2 }}>
      Statutory and policy usage tables use leave balance and approved leave requests (Aardvark data) for the period. Working-day totals exclude public holidays per employee company country (Indian employees use the South Africa holiday calendar).
    </Typography>
    <AskAardvarkHelpHint about="organisation leave and statutory status" />
  </HelpSidebar>
);

export default OrganisationLeaveHelpSidebar;
